import { logTurnstileToken } from './diagnostics.js'

// Contract: Promise<raw Turnstile token>. The core still owns /security/verify.
const loaders = new WeakMap()
const activeDocuments = new WeakSet()

function verificationError(code) {
  const error = new Error(code)
  error.name = code === 'cancelled' ? 'AbortError' : 'HumanVerificationError'
  error.code = code
  error.humanVerification = true
  return error
}

export function loadTurnstile(win = window, doc = document) {
  if (typeof win.turnstile?.render === 'function') return Promise.resolve(win.turnstile)
  if (loaders.has(win)) return loaders.get(win)
  const operation = new Promise((resolve, reject) => {
    let script
    let timer
    let settled = false
    function finish(ok) {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (script) script.onload = script.onerror = null
      if (ok && typeof win.turnstile?.render === 'function') resolve(win.turnstile)
      else {
        script?.remove()
        reject(verificationError('unavailable'))
      }
    }
    try {
      script = doc.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.onload = () => finish(true)
      script.onerror = () => finish(false)
      timer = setTimeout(() => finish(false), 15000)
      doc.head.appendChild(script)
    } catch (_) {
      finish(false)
    }
  })
  loaders.set(win, operation)
  const release = () => { if (loaders.get(win) === operation) loaders.delete(win) }
  operation.then(release, release)
  return operation
}

function messages(locale) {
  if (/^zh-(HK|TW|Hant)/i.test(locale)) return ['安全驗證', '請完成驗證後繼續。', '正在載入驗證…', '取消']
  if (/^zh/i.test(locale)) return ['安全验证', '请完成验证后继续。', '正在加载验证…', '取消']
  if (/^(ko|kn)/i.test(locale)) return ['보안 인증', '계속하려면 인증을 완료하세요.', '인증을 불러오는 중…', '취소']
  return ['Security check', 'Complete this check to continue.', 'Loading verification…', 'Cancel']
}

// The website supplies its persistent card container; standalone clients use the fallback surface.
export function showTurnstile(config, { signal, locale = 'en', container, onReady, win = window, doc = document } = {}) {
  if (signal?.aborted) return Promise.reject(verificationError('cancelled'))
  if (!doc.body || !config || typeof config.siteKey !== 'string' || !config.siteKey.trim() || config.action !== 'api_access') {
    return Promise.reject(verificationError('unavailable'))
  }
  // The core shares one pending verification. Refuse a second, independent caller.
  if (activeDocuments.has(doc)) return Promise.reject(verificationError('unavailable'))
  activeDocuments.add(doc)
  return new Promise((resolve, reject) => {
    let surface
    let mount
    let status
    let api
    let widgetId
    let timer
    let settled = false
    let received = false
    const abort = () => finish(verificationError('cancelled'))
    function removeWidget(id) {
      try { if (id !== undefined) api.remove(id) } catch (_) { /* Cleanup must not strand the caller. */ }
    }
    function finish(error, token) {
      if (settled) return
      if (signal?.aborted) error = verificationError('cancelled')
      settled = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', abort)
      removeWidget(widgetId)
      mount?.remove()
      surface?.remove()
      activeDocuments.delete(doc)
      if (error) reject(error)
      else resolve(token)
    }
    try {
      const text = messages(locale)
      mount = doc.createElement('div')
      mount.style.cssText = 'display:block;width:100%;min-height:65px;'
      if (container) {
        if (container.ownerDocument !== doc || !container.isConnected) throw verificationError('unavailable')
        // A previous interrupted attempt can leave an iframe or mount node in
        // this persistent gate.  A retry always starts from an empty container.
        container.replaceChildren()
        container.appendChild(mount)
      } else {
        surface = doc.createElement('section')
        surface.className = 'uplus-simple-turnstile'
        surface.setAttribute('role', 'region')
        surface.setAttribute('aria-label', text[0])
        surface.style.cssText = 'all:initial;display:block;position:fixed;inset:0;z-index:2147483647;overflow:auto;background:#fff;color:#172033;font:16px/1.65 system-ui,sans-serif;'
        const panel = doc.createElement('div')
        panel.style.cssText = 'display:block;box-sizing:border-box;max-width:760px;margin:40px auto;padding:0 10px;'
        const title = doc.createElement('h1')
        title.textContent = text[0]
        title.style.cssText = 'display:block;font:600 25px/1.5 system-ui,sans-serif;margin:0 0 16px;color:inherit;'
        const description = doc.createElement('p')
        description.textContent = text[1]
        description.style.cssText = 'display:block;font:inherit;color:inherit;margin:16px 0;'
        status = doc.createElement('p')
        status.setAttribute('role', 'status')
        status.textContent = text[2]
        status.style.cssText = 'display:block;font:inherit;color:inherit;margin:16px 0;'
        mount.style.cssText = 'display:block;margin:18px 0;min-height:65px;'
        const cancel = doc.createElement('button')
        cancel.type = 'button'
        cancel.textContent = text[3]
        cancel.style.cssText = 'font:inherit;cursor:pointer;padding:10px 14px;color:#172033;background:#fff;border:1px solid #64748b;border-radius:4px;'
        cancel.onclick = abort
        panel.append(title, description, status, mount, cancel)
        surface.appendChild(panel)
        doc.body.appendChild(surface)
      }
      timer = setTimeout(() => finish(verificationError('timeout')), 180000)
      signal?.addEventListener('abort', abort, { once: true })
      if (signal?.aborted) {
        abort()
        return
      }
      loadTurnstile(win, doc).then(turnstile => {
        if (settled) return
        api = turnstile
        if (status) status.textContent = ''
        try {
          // Match the passing standalone page: one render, block container and automatic language/theme.
          widgetId = api.render(mount, {
            sitekey: config.siteKey,
            action: config.action,
            theme: 'auto',
            size: 'flexible',
            'response-field': false,
            retry: 'never',
            'refresh-expired': 'manual',
            'refresh-timeout': 'manual',
            callback(token) {
              if (settled || received) return
              received = true
              void logTurnstileToken('callback', token)
              if (typeof token !== 'string' || !token.trim() || token.length > 2048) {
                finish(verificationError('invalid'))
                return
              }
              // Preserve the original bytes. Defer cleanup until this callback has returned.
              queueMicrotask(() => finish(null, token))
            },
            'error-callback': () => { if (!received) finish(verificationError('unavailable')); return true },
            'expired-callback': () => { if (!received) finish(verificationError('invalid')) },
            'timeout-callback': () => { if (!received) finish(verificationError('timeout')) }
          })
          if (settled) removeWidget(widgetId)
          else if (!received) onReady?.()
        } catch (_) {
          finish(verificationError('unavailable'))
        }
      }, () => finish(verificationError('unavailable')))
    } catch (_) {
      finish(verificationError('unavailable'))
    }
  })
}
